import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, X } from "lucide-react";
import { Button } from "./ui/button";
import { useFeatureFlag } from "../utils/featureFlags";
import api from "../utils/api";

// One-time heads-up shown to users who have Google Calendar connected,
// letting them know they can now choose which calendars QuoteSorted watches
// for busy/diary (so a shared family calendar no longer blocks job bookings).
// Persisted server-side via /calendar/dismiss-watch-notice so it doesn't
// reappear across devices.
const CalendarWatchNotice = () => {
  const navigate = useNavigate();
  const featureEnabled = useFeatureFlag("calendars_to_watch");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!featureEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/calendar/status");
        if (cancelled) return;
        if (res.data?.connected && !res.data?.watch_notice_seen) {
          setShow(true);
        }
      } catch {
        // Silent — a notice must never crash the app shell.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [featureEnabled]);

  const dismiss = async () => {
    setShow(false);
    try {
      await api.post("/calendar/dismiss-watch-notice");
    } catch {}
  };

  const goToSetting = async () => {
    setShow(false);
    try {
      await api.post("/calendar/dismiss-watch-notice");
    } catch {}
    navigate("/certificates?open=calendar-sync#calendars-to-watch");
  };

  if (!show) return null;

  return (
    <div
      className="mx-4 mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
      data-testid="calendar-watch-notice"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
        <CalendarDays className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-900">
          New: choose which Google calendars QuoteSorted watches
        </p>
        <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
          If you share a family or personal calendar with your Google account, those events can show
          on your diary and block job bookings. You can now untick them so only your work calendars
          are used for availability. Tap below to open Calendar Sync and choose which calendars we watch.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button
            size="sm"
            onClick={goToSetting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="calendar-watch-notice-cta"
          >
            Choose calendars
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            className="text-emerald-700 hover:bg-emerald-100"
            data-testid="calendar-watch-notice-dismiss"
          >
            Dismiss
          </Button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-emerald-400 hover:text-emerald-600 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="calendar-watch-notice-close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default CalendarWatchNotice;
